import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { inspectUrlSeo } from "@/lib/seo-check.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertTriangle, XCircle, ExternalLink, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_employee/medewerker/office/seo-check")({
  head: () => ({
    meta: [
      { title: "SEO-check — Medewerker" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoCheckPage,
});

type Result = Awaited<ReturnType<typeof inspectUrlSeo>>;

const PRESETS = [
  "/",
  "/access-points",
  "/advies",
  "/assortiment",
  "/blog",
  "/klantenservice",
  "/over-ons",
  "/offerte/groepenkasten",
  "/offerte/camerabewaking",
  "/laadpaal-installateur",
  "/bedenktijd",
  "/disclaimer",
];

function Row({ label, value, verdict, hint }: { label: string; value: React.ReactNode; verdict?: "ok" | "warn" | "bad" | "info"; hint?: string }) {
  const Icon = verdict === "ok" ? CheckCircle2 : verdict === "warn" ? AlertTriangle : verdict === "bad" ? XCircle : null;
  const color = verdict === "ok" ? "text-emerald-600" : verdict === "warn" ? "text-amber-600" : verdict === "bad" ? "text-red-600" : "text-muted-foreground";
  return (
    <div className="flex flex-col gap-1 border-b border-border py-3 sm:flex-row sm:items-start sm:gap-6">
      <div className="w-56 shrink-0 text-sm font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-1 items-start gap-2 break-all text-sm">
        {Icon && <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${color}`} />}
        <div className="flex-1">
          <div className="font-mono text-sm">{value ?? <span className="italic text-muted-foreground">— ontbreekt —</span>}</div>
          {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function SeoCheckPage() {
  const inspect = useServerFn(inspectUrlSeo);
  const [target, setTarget] = useState("/");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(path: string) {
    setLoading(true);
    setError(null);
    setResult(null);
    setTarget(path);
    try {
      const r = await inspect({ data: { target: path } });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const canonicalVerdict = (() => {
    if (!result || !result.ok || result.redirectLocation) return "info" as const;
    if (!result.canonical) return "bad" as const;
    if (result.allCanonicals.length > 1) return "warn" as const;
    try {
      const c = new URL(result.canonical);
      const f = new URL(result.finalUrl);
      if (c.pathname.replace(/\/$/, "") !== f.pathname.replace(/\/$/, "")) return "warn" as const;
    } catch {
      return "warn" as const;
    }
    return "ok" as const;
  })();

  const robotsVerdict = (() => {
    if (!result || !result.ok || result.redirectLocation) return "info" as const;
    const r = (result.robots ?? "").toLowerCase();
    if (r.includes("noindex")) return "warn" as const;
    if (!result.robots) return "ok" as const; // implicit index,follow
    return "ok" as const;
  })();

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">SEO-check</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Haal een pagina op via de server en toon de canonical, robots-meta en overige head-signalen die crawlers zien.
        </p>
      </header>

      <div className="rounded-lg border border-border bg-card p-4">
        <form
          className="flex flex-col gap-3 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            void run(target.trim() || "/");
          }}
        >
          <Input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="/pad of https://elvion-group.nl/pad"
            className="font-mono"
          />
          <Button type="submit" disabled={loading} className="sm:w-40">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Controleren"}
          </Button>
        </form>

        <div className="mt-3 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => void run(p)}
              className="rounded-full border border-border bg-background px-3 py-1 text-xs font-mono transition hover:bg-accent"
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && !result.ok && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {result.error}
        </div>
      )}

      {result && result.ok && (
        <div className="mt-6 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div className="min-w-0">
              <div className="truncate font-mono text-sm">{result.finalUrl}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                HTTP {result.status} · {result.durationMs} ms · {result.contentType ?? "?"}
              </div>
            </div>
            <a
              href={result.finalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              Openen <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="p-4">
            {result.redirectLocation && (
              <Row label="Redirect" verdict="info" value={<span>→ {result.redirectLocation}</span>} hint={`Server retourneerde HTTP ${result.status}. Controleer de bestemming.`} />
            )}
            <Row
              label="Canonical"
              verdict={canonicalVerdict}
              value={result.canonical ?? null}
              hint={
                result.allCanonicals.length > 1
                  ? `⚠︎ Meerdere canonicals gevonden: ${result.allCanonicals.join(", ")}`
                  : canonicalVerdict === "warn"
                    ? "Canonical wijst naar een ander pad dan de opgevraagde URL."
                    : canonicalVerdict === "bad"
                      ? "Geen canonical-tag gevonden."
                      : undefined
              }
            />
            <Row
              label="Robots (meta)"
              verdict={robotsVerdict}
              value={result.robots ?? <span className="italic text-muted-foreground">geen tag — impliciet index,follow</span>}
              hint={result.allRobots.length > 1 ? `Meerdere robots-tags: ${result.allRobots.join(" | ")}` : undefined}
            />
            <Row
              label="X-Robots-Tag (header)"
              value={result.xRobotsTag ?? <span className="italic text-muted-foreground">— niet gezet —</span>}
              verdict={result.xRobotsTag?.toLowerCase().includes("noindex") ? "warn" : "info"}
            />
            <Row label="Title" verdict={result.title ? "ok" : "bad"} value={result.title} />
            <Row label="Meta description" verdict={result.description ? "ok" : "warn"} value={result.description} />
            <Row label="H1" verdict={result.h1s.length === 1 ? "ok" : result.h1s.length === 0 ? "bad" : "warn"} value={result.h1s.length === 0 ? null : result.h1s.join(" | ")} hint={result.h1s.length > 1 ? `Meerdere H1's (${result.h1s.length}).` : undefined} />
            <Row label="og:url" value={result.ogUrl} verdict={result.ogUrl ? "ok" : "info"} />
            <Row label="og:title" value={result.ogTitle} verdict={result.ogTitle ? "ok" : "info"} />
            <Row label="og:description" value={result.ogDescription} verdict={result.ogDescription ? "ok" : "info"} />
            <Row label="og:type" value={result.ogType} verdict="info" />
          </div>
        </div>
      )}
    </div>
  );
}
